import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Building2, Info, TrendingUp, Users, Activity } from 'lucide-react';
import { useTranslation } from '@/components/utils/translations';
import { translateIncumbentSentiment } from '@/lib/accountClassificationLabels';
import { canonicalizeSentiment } from '@/utils/procurementDealUtils';

const getFitScoreInfo = (score) => {
  const numScore = Number(score ?? 0) || 0;
  if (numScore >= 85) return { label: "Excellent", color: "bg-green-100 text-green-800" };
  if (numScore >= 75) return { label: "Strong", color: "bg-blue-100 text-blue-800" };
  if (numScore >= 65) return { label: "Moderate", color: "bg-yellow-100 text-yellow-800" };
  return { label: "Low", color: "bg-gray-100 text-gray-800" };
};

const getSentimentColor = (sentiment) => {
  const bucket = canonicalizeSentiment(sentiment);
  if (bucket === 'positive') return 'bg-green-100 text-green-800';
  if (bucket === 'neutral') return 'bg-blue-100 text-blue-800';
  if (bucket === 'negative') return 'bg-red-100 text-red-800';
  return 'bg-gray-100 text-gray-800';
};

const DepartmentCard = ({ dept }) => {
  const { t, language } = useTranslation();
  const fit = getFitScoreInfo(dept.department_fit_score);
  
  const parseSignals = (signals) => {
    if (!signals) return [];
    const matches = signals.match(/\d\)\s*[^]+?(?=(?:\s*\d\)\s*)|$)/g) || [];
    return matches.map((s, i) => ({ 
      id: i + 1, 
      text: s.replace(/^\d\)\s*/, '').trim() 
    }));
  };
  
  const signals = parseSignals(dept.department_signals);

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3 px-4 pt-4 md:px-6 md:pt-6">
        {/* Mobile: Stack vertically, Desktop: Side by side */}
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          {/* Department name with icon */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0">
              <Building2 className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
            </div>
            <CardTitle className="text-base md:text-lg font-semibold text-[#0A1A2F] break-words">
              {dept.department}
            </CardTitle>
          </div>
          
          {/* Badges: Stack on mobile, flex-shrink-0 on desktop */}
          <div className="flex flex-row flex-wrap gap-2 md:flex-col md:items-end md:flex-shrink-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="cursor-help">
                  <Badge className={`${fit.color} text-xs md:text-sm px-2 py-1 md:px-3`}>
                    {fit.label} ({Number(dept.department_fit_score) || 0})
                  </Badge>
                </div>
              </TooltipTrigger>
              <TooltipContent 
                side="bottom" 
                align="start"
                className="max-w-[90vw] md:max-w-md bg-[#0A1A2F] text-white border-0 shadow-lg"
              >
                <p className="text-sm font-semibold text-[#00C2C2] mb-1">Fit Score Rationale</p>
                <p className="text-xs leading-relaxed">{dept.department_fit_rationale || 'No rationale available.'}</p>
              </TooltipContent>
            </Tooltip>
            {dept.incumbent_sentiment_department && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="cursor-help">
                    <Badge className={`${getSentimentColor(dept.incumbent_sentiment_department)} text-xs px-2 py-0.5`}>
                      {translateIncumbentSentiment(language, dept.incumbent_sentiment_department)}
                    </Badge>
                  </div>
                </TooltipTrigger>
                <TooltipContent 
                  side="bottom" 
                  align="start"
                  className="max-w-[90vw] md:max-w-md bg-[#0A1A2F] text-white border-0 shadow-lg"
                >
                  <p className="text-sm font-semibold text-purple-300 mb-1">Sentiment Rationale</p>
                  <p className="text-xs leading-relaxed">
                    {dept.sentiment_rationale_department || 'No rationale available.'}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3 md:space-y-4 px-4 pb-4 md:px-6 md:pb-6">
        {/* Booking Channels */}
        {dept.booking && (
          <div>
            <div className="flex items-center gap-2 mb-1.5 md:mb-2">
              <Activity className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#5C6B7A] flex-shrink-0" />
              <h4 className="text-xs md:text-sm font-semibold text-[#0A1A2F]">Booking Channels</h4>
            </div>
            <p className="text-xs md:text-sm text-[#5C6B7A] leading-relaxed pl-5 md:pl-6">
              {dept.booking}
            </p>
          </div>
        )}

        {/* Incumbent */}
        {dept.incumbent_name_department && (
          <div>
            <div className="flex items-center gap-2 mb-1.5 md:mb-2">
              <Users className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#5C6B7A] flex-shrink-0" />
              <h4 className="text-xs md:text-sm font-semibold text-[#0A1A2F]">Current Incumbent</h4>
            </div>
            <p className="text-xs md:text-sm text-[#5C6B7A] pl-5 md:pl-6 break-words">
              {dept.incumbent_name_department}
            </p>
          </div>
        )}

        {/* Volume */}
        {dept.department_volume && (
          <div>
            <div className="flex items-center gap-2 mb-1.5 md:mb-2">
              <TrendingUp className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#5C6B7A] flex-shrink-0" />
              <h4 className="text-xs md:text-sm font-semibold text-[#0A1A2F]">Volume & Capacity</h4>
            </div>
            <p className="text-xs md:text-sm text-[#5C6B7A] leading-relaxed pl-5 md:pl-6">
              {dept.department_volume}
            </p>
          </div>
        )}

        {/* Key Signals */}
        {signals.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-1.5 md:mb-2">
              <Info className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#5C6B7A] flex-shrink-0" />
              <h4 className="text-xs md:text-sm font-semibold text-[#0A1A2F]">Key Signals</h4>
            </div>
            <ul className="space-y-1.5 md:space-y-2 pl-5 md:pl-6">
              {signals.map((signal) => (
                <li key={signal.id} className="text-xs md:text-sm text-[#5C6B7A] leading-relaxed flex gap-1.5 md:gap-2">
                  <span className="font-medium text-[#0A1A2F] flex-shrink-0">{signal.id})</span>
                  <span className="break-words">{signal.text}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function DepartmentsList({ departments }) {
  if (!departments || departments.length === 0) {
    return (
      <div className="text-center py-12">
        <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
        <h3 className="text-base md:text-lg font-semibold text-[#0A1A2F] mb-2">No Departments</h3>
        <p className="text-sm text-[#5C6B7A]">No department data available for this account.</p>
      </div>
    );
  }

  const sortedDepartments = [...departments].sort(
    (a, b) => (Number(b.department_fit_score) || 0) - (Number(a.department_fit_score) || 0)
  );

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg md:text-xl font-bold text-[#0A1A2F]">Departments</h2>
          <p className="text-xs md:text-sm text-[#5C6B7A] mt-1">
            {departments.length} department{departments.length !== 1 ? 's' : ''} analyzed
          </p>
        </div>
      </div>

      {/* Single column on mobile, 2 columns on large screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {sortedDepartments.map((dept) => (
          <DepartmentCard key={dept.id || dept.department} dept={dept} />
        ))}
      </div>
    </div>
  );
}